import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { SttDialog } from '../../sporttia/all';
import { latinize, requestAdHoc } from '../../lib/utils';
import { usePOSConfig } from '../../lib/hooks';
import Colors from '../../styles/Colors';
import { PosPaymentParams } from '../../types/payment';

const PosStatuses = {
    ok: 'OK',
    error: 'ERROR',
    conn_error: 'CONN_ERROR',
} as const;

type PosStatus = (typeof PosStatuses)[keyof typeof PosStatuses] | null;

type POSPaymentDialogProps = {
    paymentData: PosPaymentParams | null;
    open: boolean;
    onClose: () => void;
    onSuccess?: (ticket?: unknown, returnParams?: unknown) => void;
};

type POSResponse = {
    status: string;
    message?: string;
    ticket?: unknown;
    resultCode?: string;
    resultMessage?: string;
};

/**
 * Diálogo para realizar el pago con datáfono.
 * @param paymentData parámetros para el pago en la forma {amount, concept, idTpv, returnParams, sc}
 * @param open modal abierto
 * @param onClose evento de cierre
 * @param onSuccess evento de pago exitoso
 * @returns {React.ReactElement}
 */
export default function POSPaymentDialog({
    paymentData,
    open,
    onClose,
    onSuccess,
}: POSPaymentDialogProps): React.ReactElement {
    const SELF_CLOSING_SECONDS = 30;

    // hooks
    const { t } = useTranslation();
    const [status, setStatus] = useState<PosStatus>(null);
    const [errorMessage, setErrorMessage] = useState('');

    // timer se usa para la cuenta atrás de "esta ventana se cerrará en X segundos"
    const [timer, setTimer] = useState(-1);

    // ip del dispositivo guardada en localStorage (ver /pages/sc/configuration/ConfigurationPOS.js)
    const ip = localStorage.getItem('CM5DeviceAddress');
    const setupPOSConfig = usePOSConfig('PAYCOMET');

    const POSConfig = setupPOSConfig(ip);

    // El handshake establece comunicación con el datáfono
    const handShake = (): Promise<void> =>
        new Promise((resolve: any, reject: any) => {
            // Si no está establecida la IP del datáfono no hay nada que rascar.
            if (!ip) {
                reject(new Error(t('config.pos.deviceIpNotSet')));
            } else {
                // Lanzar la petición de handshake
                requestAdHoc(
                    POSConfig.init.method,
                    POSConfig.init.uri,
                    POSConfig.init.params() as unknown as Record<
                        string,
                        unknown
                    >,
                )
                    .then((r: unknown) => {
                        const reponse = POSConfig.init.response(
                            r as {
                                resultCode: unknown;
                                resultMessage: unknown;
                            },
                        ) as POSResponse;
                        if (reponse.status !== 'OK') {
                            // el handshake crujió
                            reject(new Error(t('config.pos.deviceTestError')));
                        } else {
                            resolve();
                        }
                    })
                    // Si el datáfono no contesta, requestAdHoc rechaza con el
                    // mensaje del navegador («Failed to fetch»), que el centro
                    // confunde con un error de Sporttia; el original va a consola.
                    .catch((err: unknown) => {
                        console.error('Error al conectar con el datáfono', err);
                        reject(
                            new Error(t('posPaymentDialog.connectionError')),
                        );
                    });
            }
        });

    // Hacer una petición al datáfono
    const requestPosPayment = (): void => {
        const params = {
            amount: paymentData!.amount,
            concept: latinize(paymentData!.concept),
            idTpv: paymentData!.idTpv,
        };

        // Lanzar la petición de pago
        requestAdHoc(
            POSConfig.transaction.method,
            POSConfig.transaction.uri,
            POSConfig.transaction.params(params),
        )
            .then((r: unknown) => {
                const response = POSConfig.transaction.response(
                    r as {
                        resultCode: unknown;
                        ticket: { Status: unknown };
                        resultMessage: unknown;
                    },
                ) as POSResponse;

                if (response?.status === 'OK') {
                    // Callback: llamar pasando los parámetros dados por returnParams (se usan fuera para hacer cosas)
                    setStatus(PosStatuses.ok);
                    setErrorMessage('');
                    onSuccess?.(response.ticket, paymentData?.returnParams);
                } else {
                    setStatus(PosStatuses.error);
                    setErrorMessage(response.message || '');
                }
                setTimer(SELF_CLOSING_SECONDS);
            })
            .catch((err: unknown) => {
                console.log(err);
            });
    };

    // Actualizar valores al recibir un nuevo 'paymentData' y lanzar el pago
    useEffect(() => {
        if (paymentData) {
            setStatus(null);
            setErrorMessage('');
            setTimer(-1);

            // Handshake, y si ha ido bien, entonces pago.
            handShake()
                .then(() => {
                    setStatus(null);
                    requestPosPayment();
                })
                .catch((err: Error) => {
                    setStatus(PosStatuses.conn_error);
                    setErrorMessage(err.message);
                });
        }
    }, [paymentData]);

    // Actualizar la cuenta atrás. Devuelve un callback que se llamará al desmontar el componente.
    useEffect(() => {
        let timeout: NodeJS.Timeout | undefined;
        if (timer > 0) {
            timeout = setTimeout(() => {
                setTimer(timer - 1);
            }, 1000);
        } else if (timer === 0) {
            onClose();
        }
        // No need to clear timeout in timer === -1 case since no timeout was created

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [timer]);

    function requestPrintCopy(): void {
        // Lanzar la petición de imprimir copia
        requestAdHoc(
            POSConfig.printLastTransaction.method,
            POSConfig.printLastTransaction.uri,
            undefined,
        )
            .then((response: unknown) => {
                const res = response as {
                    resultCode?: string;
                    resultMessage?: string;
                };
                if (res.resultCode) {
                    if (parseInt(res.resultCode) !== 0) {
                        setErrorMessage(res.resultMessage || '');
                    }
                }
            })
            .catch((err: unknown) => {
                console.log(err);
            });

        setTimer(-1);
    }

    return (
        <SttDialog
            title={t('posPaymentDialog.title')}
            maxWidth="sm"
            open={open}
            onClose={onClose}
            content={
                <Box>
                    {!status ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Typography>
                                {t('posPaymentDialog.waitingForPos')}...
                            </Typography>
                            <Box mt={2}>
                                <CircularProgress />
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                        >
                            {status === PosStatuses.ok && (
                                <>
                                    <Typography>
                                        {t(
                                            'posPaymentDialog.operationComplete',
                                        )}
                                    </Typography>
                                    <Box mt={2}>
                                        <Typography>
                                            {t('Result')}:{' '}
                                            <span
                                                style={{ color: Colors.green }}
                                            >
                                                {status}
                                            </span>
                                        </Typography>
                                    </Box>
                                    <Box mt={2}>
                                        <Button
                                            onClick={requestPrintCopy}
                                            variant="contained"
                                            color="primary"
                                        >
                                            {t('pos.printCopy')}
                                        </Button>
                                    </Box>
                                    {errorMessage !== '' && (
                                        <Box mt={2}>
                                            <Typography
                                                style={{ color: Colors.red }}
                                            >
                                                {errorMessage}
                                            </Typography>
                                        </Box>
                                    )}
                                    {timer > 0 && (
                                        <Box mt={2}>
                                            <Typography>
                                                {t(
                                                    'posPaymentDialog.selfClosingIn',
                                                    { seconds: timer },
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                            {status === PosStatuses.error && (
                                <>
                                    <Typography>
                                        {t(
                                            'posPaymentDialog.operationComplete',
                                        )}
                                    </Typography>
                                    <Box mt={2}>
                                        <Typography>
                                            {t('Result')}:{' '}
                                            <span style={{ color: Colors.red }}>
                                                {status}
                                            </span>
                                        </Typography>
                                        {errorMessage !== '' && (
                                            <Box mt={2}>
                                                <Typography
                                                    style={{
                                                        color: Colors.red,
                                                    }}
                                                >
                                                    {errorMessage}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                    {timer > 0 && (
                                        <Box mt={2}>
                                            <Typography>
                                                {t(
                                                    'posPaymentDialog.selfClosingIn',
                                                    { seconds: timer },
                                                )}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                            {status === PosStatuses.conn_error && (
                                <>
                                    <Typography style={{ color: Colors.red }}>
                                        {errorMessage}
                                    </Typography>
                                    <Box mt={2}>
                                        <Button
                                            onClick={() => {
                                                handShake()
                                                    .then(() => {
                                                        setStatus(null);
                                                        requestPosPayment();
                                                    })
                                                    .catch((err: Error) => {
                                                        setStatus(
                                                            PosStatuses.conn_error,
                                                        );
                                                        setErrorMessage(
                                                            err.message,
                                                        );
                                                    });
                                            }}
                                            color="primary"
                                        >
                                            {t('pos.retry')}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            }
            buttons={[
                {
                    type: 'close',
                    onClick: onClose,
                },
            ]}
        />
    );
}
